/**
 * Pure helpers of the chat entry points: prompt, topic, source and variant
 * normalisation, `#dhsv-chat` hash links and the teaser frequency rule.
 *
 * DOM-free on purpose: the host (`iframe-host.ts`) reads attributes and the
 * options object, these functions decide what the values mean. One parser
 * serves the public API and the delegated triggers, so `data-dhsv-chat-*` and
 * `DhsvChat.open( { ... } )` cannot drift apart.
 */

export const PROMPT_MAX_LENGTH = 500;
export const TOPIC_MAX_LENGTH = 64;
// A prompt delivered less than this long ago counts as "still being submitted":
// the iframe submits an opened message after its own open delay, so the chat
// status has not changed yet.
export const PROMPT_SETTLE_MS = 600;
export const TEASER_STORAGE_KEY = 'dhsv100-chat-teaser';

const HASH_TRIGGER = '#dhsv-chat';
const SOURCE_PATTERN = /^[a-z][a-z0-9_-]{0,31}$/;
const DAY_MS = 86_400_000;

export type EntryVariant = 'default' | 'sidebar' | 'fullscreen' | 'island';
export type EntryVariantOrUnset = EntryVariant | 'unset';

export type OpenOptions = {
	prompt?: string;
	topic?: string;
	variant?: string;
	source?: string;
};

export type ParsedOpenOptions = {
	variant: EntryVariantOrUnset;
	prompt: string;
	topic?: string;
	source?: string;
};

function truncateCodePoints( value: string, max: number ): string {
	const points = Array.from( value );
	return points.length > max ? points.slice( 0, max ).join( '' ) : value;
}

/**
 * Text a visitor could have typed: string only, whitespace runs (including line
 * breaks and tabs) collapsed to one space, remaining control characters
 * dropped, cut at `PROMPT_MAX_LENGTH` code points. Never parsed as markup.
 *
 * @param {unknown} value Raw attribute or option value.
 */
export function normalizePrompt( value: unknown ): string {
	if ( typeof value !== 'string' ) {
		return '';
	}
	const cleaned = value
		.replace( /\s+/g, ' ' )
		// eslint-disable-next-line no-control-regex
		.replace( /[\u0000-\u001F\u007F-\u009F]/g, '' )
		.trim();
	return truncateCodePoints( cleaned, PROMPT_MAX_LENGTH ).trim();
}

/**
 * Tracking topic: trimmed, 64 code points, `undefined` when empty.
 *
 * @param {unknown} value Raw attribute or option value.
 */
export function normalizeTopic( value: unknown ): string | undefined {
	if ( typeof value !== 'string' ) {
		return undefined;
	}
	const topic = truncateCodePoints( value.trim(), TOPIC_MAX_LENGTH ).trim();
	return topic === '' ? undefined : topic;
}

/**
 * A source name: the pattern, else the fallback.
 *
 * @param {unknown} value    Raw attribute or option value.
 * @param {string}  fallback Used for an absent or invalid value.
 */
export function normalizeSource( value: unknown, fallback: string ): string {
	return typeof value === 'string' && SOURCE_PATTERN.test( value )
		? value
		: fallback;
}

/**
 * An absent or invalid variant is `unset`, never `default`: the caller decides
 * what unset means (the API keeps `default`, entry points apply the surface rule).
 *
 * @param {unknown} value Raw attribute or option value.
 */
export function normalizeVariant( value: unknown ): EntryVariantOrUnset {
	return value === 'default' ||
		value === 'sidebar' ||
		value === 'fullscreen' ||
		value === 'island'
		? value
		: 'unset';
}

/**
 * The surface an entry point opens. An explicit variant wins; otherwise the
 * island card in sticky-bar mode on a desktop viewport (the surface the bar
 * itself opens) and the default panel everywhere else.
 *
 * @param {EntryVariantOrUnset} variant              Parsed variant.
 * @param {Object}              context              Page state.
 * @param {string}              context.launcherMode `bubble` or `sticky_bar`.
 * @param {boolean}             context.mobileShell  Viewport is in the mobile shell.
 * @param {boolean}             context.forced       Container exists only because a block asked.
 */
export function resolveEntryVariant(
	variant: EntryVariantOrUnset,
	context: { launcherMode: string; mobileShell: boolean; forced: boolean }
): EntryVariant {
	if ( variant !== 'unset' ) {
		return variant;
	}
	return context.launcherMode === 'sticky_bar' &&
		! context.mobileShell &&
		! context.forced
		? 'island'
		: 'default';
}

/**
 * Chat hash link: `#dhsv-chat`, optionally `/<variant>`, optionally `=` plus
 * the prompt (`#dhsv-chat/sidebar=Hallo`). An unknown variant segment is
 * still a trigger and reports `unset`.
 *
 * The prompt is `encodeURIComponent()` text, but the Navigation block hands
 * over an already decoded value, so both pass; a throwing decode (a stray `%`)
 * uses the text literally.
 *
 * @param {string|null} href The `href` attribute, not the resolved property.
 */
export function parseHashTrigger(
	href: string | null
): { prompt: string; variant: EntryVariantOrUnset } | null {
	if ( typeof href !== 'string' || ! href.startsWith( HASH_TRIGGER ) ) {
		return null;
	}
	let rest = href.slice( HASH_TRIGGER.length );
	let variant: EntryVariantOrUnset = 'unset';
	if ( rest.startsWith( '/' ) ) {
		const end = rest.indexOf( '=' );
		const segment = end === -1 ? rest.slice( 1 ) : rest.slice( 1, end );
		variant = normalizeVariant( segment );
		rest = end === -1 ? '' : rest.slice( end );
	}
	if ( rest === '' ) {
		return { prompt: '', variant };
	}
	if ( ! rest.startsWith( '=' ) ) {
		return null;
	}
	const encoded = rest.slice( 1 );
	let decoded = encoded;
	try {
		decoded = decodeURIComponent( encoded );
	} catch {
		decoded = encoded;
	}
	return { prompt: normalizePrompt( decoded ), variant };
}

/**
 * The one parser behind `open( variant | options )` and the delegated triggers.
 *
 * @param {unknown} arg A variant string or an options object.
 */
export function parseOpenOptions( arg: unknown ): ParsedOpenOptions {
	if ( typeof arg === 'string' ) {
		return { variant: normalizeVariant( arg ), prompt: '' };
	}
	if ( ! arg || typeof arg !== 'object' ) {
		return { variant: 'unset', prompt: '' };
	}
	const options = arg as Record< string, unknown >;
	const source = normalizeSource( options.source, '' );
	const topic = normalizeTopic( options.topic );
	return {
		variant: normalizeVariant( options.variant ),
		prompt: normalizePrompt( options.prompt ),
		...( topic ? { topic } : {} ),
		...( source ? { source } : {} ),
	};
}

/**
 * Integer inside `min`..`max`; anything non-numeric is the fallback.
 *
 * @param {unknown} value    Raw value.
 * @param {number}  min      Lower bound.
 * @param {number}  max      Upper bound.
 * @param {number}  fallback Used for a non-numeric value.
 */
export function clampInt(
	value: unknown,
	min: number,
	max: number,
	fallback: number
): number {
	const parsed =
		typeof value === 'number' ? value : Number( String( value ).trim() );
	if (
		value === null ||
		value === undefined ||
		! Number.isFinite( parsed )
	) {
		return fallback;
	}
	return Math.min( max, Math.max( min, Math.round( parsed ) ) );
}

/**
 * May the teaser show, given the stored timestamp of its last showing?
 *
 * Absent, garbage, negative and far-future (clock change) values count as
 * "never shown"; a frequency of 0 means no limit.
 *
 * @param {string|null} stored        Raw `localStorage` value.
 * @param {number}      now           `Date.now()`.
 * @param {number}      frequencyDays Window in days.
 */
export function shouldShowTeaser(
	stored: string | null,
	now: number,
	frequencyDays: number
): boolean {
	if ( frequencyDays <= 0 || stored === null ) {
		return true;
	}
	const timestamp = Number( stored );
	if (
		stored.trim() === '' ||
		! Number.isFinite( timestamp ) ||
		timestamp <= 0 ||
		timestamp > now + DAY_MS
	) {
		return true;
	}
	return now - timestamp > frequencyDays * DAY_MS;
}
